import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPersonToPortal } from '@fortawesome/pro-duotone-svg-icons/faPersonToPortal';
import { faPersonFromPortal } from '@fortawesome/pro-duotone-svg-icons/faPersonFromPortal';
import { faGrid2Plus } from '@fortawesome/pro-duotone-svg-icons/faGrid2Plus';
import { faGrid2Minus } from '@fortawesome/pro-duotone-svg-icons/faGrid2Minus';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivityLogEvent, ActivityLogStore } from '../../../../state/activity-log.repository';
import { UserSettingsStore } from '../../../../state/user-settings.repository';
import { formatRelativeTime } from '../../../../shared/pipes/relative-time.pipe';
import { LanguageService } from '../../../services/language.service';

type RenderedRow = {
  id: string;
  icon: typeof faPersonToPortal;
  html: string;
  time: string;
};

// Rolling window of events shown in the log.
const ACTIVITY_WINDOW_MS = 24 * 60 * 60 * 1000;

// Newest N events rendered. There is no virtual scroller here and the parent
// re-measures `scrollHeight` whenever the log grows, so an unbounded list in a
// churning room costs real layout time. The attendance export still covers the
// full history.
const MAX_RENDERED_EVENTS = 200;

// Distance from the bottom (px) within which the list still counts as "at the
// bottom" and keeps following new entries. Absorbs sub-pixel rounding.
const STICK_THRESHOLD_PX = 24;

@Component({
  selector: 'andor-activity-log',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, TranslatePipe],
  templateUrl: './activity-log.html',
  styleUrl: './activity-log.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityLog {
  private _store = inject(ActivityLogStore);
  private _userSettings = inject(UserSettingsStore);
  private _translate = inject(TranslateService);
  private _locale = inject(LanguageService).locale;

  @ViewChild('list') listRef?: ElementRef<HTMLDivElement>;

  private _roomId = toSignal(this._userSettings.roomId$, { initialValue: '' });
  private _events = toSignal(this._store.events$, { initialValue: [] as ActivityLogEvent[] });

  // Whether the list is parked at its newest entry. Flipped by `onScroll` so
  // reading history isn't interrupted by an incoming join/leave.
  private _stickToBottom = true;

  // Chronological feed scoped to the currently joined room and the last 24
  // hours: one line per raw event, oldest first, newest at the bottom. The
  // store keeps a 2-day TTL, so we narrow to a rolling 24h window here to
  // avoid surfacing stale events from an earlier session.
  rendered = computed<RenderedRow[]>(() => {
    const room = this._roomId();
    // Rows are rendered text; re-render them in a newly chosen language.
    const locale = this._locale();
    const cutoff = Date.now() - ACTIVITY_WINDOW_MS;
    return this._events()
      .filter((e) => e.roomId === room && e.time.getTime() >= cutoff)
      .sort((a, b) => a.time.getTime() - b.time.getTime() || a.id.localeCompare(b.id))
      .slice(-MAX_RENDERED_EVENTS)
      .map((event) => this._renderRow(event, locale));
  });

  // Called by `ConferencePanel` after it has measured the actions
  // panel and capped this component's host height — scrolling only works once
  // the cap is in place.
  scrollToNewest(): void {
    const list = this.listRef?.nativeElement;
    if (list && this._stickToBottom) list.scrollTop = list.scrollHeight;
  }

  onScroll(): void {
    const list = this.listRef?.nativeElement;
    if (!list) return;
    this._stickToBottom =
      list.scrollHeight - list.scrollTop - list.clientHeight <= STICK_THRESHOLD_PX;
  }

  private _renderRow(event: ActivityLogEvent, locale: string): RenderedRow {
    return {
      id: event.id,
      icon: this._iconFor(event),
      html: this._buildText(event),
      time: formatRelativeTime(event.time, this._translate, locale),
    };
  }

  private _iconFor(event: ActivityLogEvent): typeof faPersonToPortal {
    if (event.scope === 'breakout') return event.kind === 'joined' ? faGrid2Plus : faGrid2Minus;
    return event.kind === 'joined' ? faPersonToPortal : faPersonFromPortal;
  }

  // Translation key strings double as English fallback — `TranslateService`
  // returns the key verbatim when no translation exists.
  private _buildText(event: ActivityLogEvent): string {
    const name = this._bold(event.name);
    if (event.scope === 'breakout') {
      const room = this._bold(event.breakoutRoom ?? '');
      const key =
        event.kind === 'joined'
          ? '[name] joined Breakout Room [room]'
          : '[name] left Breakout Room [room]';
      return this._translate.instant(key, { name, room });
    }
    return this._translate.instant(event.kind === 'joined' ? '[name] joined' : '[name] left', {
      name,
    });
  }

  private _bold(text: string): string {
    return `<b>${text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</b>`;
  }
}
